/**
 * Utilitário centralizado para tratamento de erros de Edge Functions
 * Traduz códigos HTTP e mensagens de erro para mensagens amigáveis ao usuário
 */

export interface EdgeFunctionError {
  status?: number;
  message: string;
  title: string;
  action?: {
    label: string;
    path: string;
  };
}

/**
 * Extrai e traduz erros de Edge Functions do Supabase
 */
export function handleEdgeFunctionError(
  error: any,
  data: any,
  context: 'task' | 'admin' | 'auth' | 'general' = 'general'
): EdgeFunctionError {
  // Verificar se há erro no data (resposta da função)
  if (data?.error) {
    return translateError(data.error, data, context);
  }

  // Verificar se há erro no objeto error
  if (error?.message) {
    return translateError(error.message, error, context);
  }

  // Erro genérico
  return {
    title: 'Erro inesperado',
    message: 'Ocorreu um erro inesperado. Tente novamente mais tarde.',
  };
}

/**
 * Traduz mensagens de erro para português com contexto
 */
function translateError(errorMsg: string, errorData: any, context: string): EdgeFunctionError {
  const errorLower = errorMsg.toLowerCase();

  // Erros de créditos (402, 429)
  if (errorLower.includes('insufficient credits') || errorMsg === 'Insufficient credits') {
    return {
      status: 402,
      title: 'Créditos insuficientes',
      message: 'Você não tem créditos suficientes para criar esta task.',
      action: {
        label: 'Comprar créditos',
        path: '/dashboard/creditos',
      },
    };
  }

  if (errorLower.includes('daily credit limit') || errorLower.includes('daily limit')) {
    return {
      status: 429,
      title: 'Limite diário atingido',
      message: 'Você atingiu o limite de 2 créditos por dia. Este limite é válido nos primeiros 7 dias após a compra. Tente novamente amanhã.',
    };
  }

  // Erros de rate limit (429)
  if (errorLower.includes('rate limit')) {
    return {
      status: 429,
      title: 'Limite de requisições atingido',
      message: 'Você atingiu o limite de 10 tasks por hora. Aguarde alguns minutos e tente novamente.',
    };
  }

  // Erros de autenticação (401, 403)
  if (errorLower.includes('unauthorized') || errorLower.includes('no authorization')) {
    return {
      status: 401,
      title: 'Não autorizado',
      message: 'Sua sessão expirou. Faça login novamente.',
      action: {
        label: 'Fazer login',
        path: '/login',
      },
    };
  }

  if (errorLower.includes('admin access required')) {
    return {
      status: 403,
      title: 'Acesso negado',
      message: 'Você não tem permissão de administrador para acessar este recurso.',
    };
  }

  // Erros de validação (400)
  if (errorLower.includes('product_name is required')) {
    return {
      status: 400,
      title: 'Campo obrigatório',
      message: 'O nome do produto é obrigatório.',
    };
  }

  if (errorLower.includes('product_name must be less than')) {
    return {
      status: 400,
      title: 'Nome muito longo',
      message: 'O nome do produto deve ter no máximo 200 caracteres.',
    };
  }

  if (errorLower.includes('product_description must be less than')) {
    return {
      status: 400,
      title: 'Descrição muito longa',
      message: 'A descrição do produto deve ter no máximo 2000 caracteres.',
    };
  }

  if (errorLower.includes('invalid image format') || errorLower.includes('invalid json')) {
    return {
      status: 400,
      title: 'Formato inválido',
      message: 'O formato do arquivo enviado é inválido. Use imagens PNG, JPG ou WEBP.',
    };
  }

  if (errorLower.includes('only tasks with error or expired')) {
    return {
      status: 400,
      title: 'Reprocessamento não permitido',
      message: 'Apenas tasks com erro ou expiradas podem ser reprocessadas.',
    };
  }

  // Erros de não encontrado (404)
  if (errorLower.includes('not found') || errorLower.includes('task not found')) {
    return {
      status: 404,
      title: 'Não encontrado',
      message: context === 'task'
        ? 'Task não encontrada ou você não tem permissão para acessá-la.'
        : 'Recurso não encontrado.',
    };
  }

  if (errorLower.includes('user profile not found')) {
    return {
      status: 404,
      title: 'Perfil não encontrado',
      message: 'Seu perfil de usuário não foi encontrado. Entre em contato com o suporte.',
    };
  }

  // Erros de servidor (500)
  if (errorLower.includes('failed to upload image')) {
    return {
      status: 500,
      title: 'Erro no upload',
      message: 'Não foi possível fazer upload da imagem. Verifique sua conexão e tente novamente.',
    };
  }

  if (errorLower.includes('failed to process image')) {
    return {
      status: 500,
      title: 'Erro ao processar imagem',
      message: 'Não foi possível processar a imagem. Tente usar outra imagem ou um formato diferente.',
    };
  }

  if (errorLower.includes('photorealistic people') || (errorLower.includes('photorealistic') && errorLower.includes('people'))) {
    return {
      status: 400,
      title: 'Imagem não permitida',
      message: 'A OpenAI não permite o upload de imagens contendo pessoas fotorrealistas. Por favor, utilize uma imagem do produto sem pessoas ou com ilustrações/desenhos.',
    };
  }

  if (errorLower.includes('failed to check credits')) {
    return {
      status: 500,
      title: 'Erro ao verificar créditos',
      message: 'Não foi possível verificar seu saldo de créditos. Tente novamente.',
    };
  }

  if (errorLower.includes('failed to debit credit')) {
    return {
      status: 500,
      title: 'Erro ao debitar crédito',
      message: 'Não foi possível debitar o crédito. Tente novamente ou entre em contato com o suporte.',
    };
  }

  if (errorLower.includes('failed to create task') || errorLower.includes('failed to reset task')) {
    return {
      status: 500,
      title: 'Erro ao criar task',
      message: 'Não foi possível criar a task. Tente novamente em alguns instantes.',
    };
  }

  if (errorLower.includes('failed to fetch tasks')) {
    return {
      status: 500,
      title: 'Erro ao carregar tasks',
      message: 'Não foi possível carregar suas tasks. Tente atualizar a página.',
    };
  }

  if (errorLower.includes('failed to fetch task')) {
    return {
      status: 500,
      title: 'Erro ao carregar task',
      message: 'Não foi possível carregar os detalhes da task. Tente novamente.',
    };
  }

  // Erros do admin
  if (errorLower.includes('email is required')) {
    return {
      status: 400,
      title: 'Email obrigatório',
      message: 'Digite um email para buscar o usuário.',
    };
  }

  if (errorLower.includes('invalid action')) {
    return {
      status: 400,
      title: 'Ação inválida',
      message: 'A ação solicitada não é válida.',
    };
  }

  // Erro genérico de servidor
  if (errorLower.includes('internal server error') || errorLower.includes('an error occurred')) {
    return {
      status: 500,
      title: 'Erro no servidor',
      message: 'Ocorreu um erro no servidor. Tente novamente em alguns instantes.',
    };
  }

  // Erro genérico do Supabase
  if (errorLower.includes('edge function returned a non-2xx status code')) {
    return {
      status: 500,
      title: 'Erro na operação',
      message: 'Não foi possível completar a operação. Tente novamente.',
    };
  }

  // Fallback - retornar mensagem original se não houver tradução
  return {
    title: 'Erro',
    message: errorMsg || 'Ocorreu um erro inesperado. Tente novamente.',
  };
}

/**
 * Formata erro para exibição em toast
 */
export function formatErrorForToast(error: EdgeFunctionError) {
  return {
    title: error.title,
    description: error.message,
    variant: 'destructive' as const,
  };
}

/**
 * Interface para erros traduzidos de tasks
 */
export interface TranslatedTaskError {
  message: string;
  type: 'quota' | 'photorealistic' | 'rate_limit' | 'moderation' | 'generic';
  showContactAdmin?: boolean;
}

/**
 * Traduz mensagens de erro salvas nas tasks para mensagens amigáveis
 */
export function translateTaskErrorMessage(errorMessage: string): TranslatedTaskError {
  const lower = errorMessage.toLowerCase();

  // Erro de quota/limite da API OpenAI (429)
  if (lower.includes('429') || lower.includes('insufficient_quota') ||
    lower.includes('quota') || lower.includes('exceeded') ||
    lower.includes('limite de cota')) {
    return {
      message: 'O sistema atingiu o limite de cota da API de IA. Por favor, contate o administrador do sistema.',
      type: 'quota',
      showContactAdmin: true
    };
  }

  // Erro de pessoas fotorrealistas
  if (lower.includes('photorealistic') && lower.includes('people')) {
    return {
      message: 'A OpenAI não permite o upload de imagens contendo pessoas fotorrealistas. Por favor, utilize uma imagem do produto sem pessoas ou com ilustrações/desenhos.',
      type: 'photorealistic'
    };
  }

  // Erro de moderação de imagem (conteúdo sensível)
  if (lower.includes('conteúdo sensível') || lower.includes('imagem enviada contém') ||
    lower.includes('sexual') || lower.includes('violência') || lower.includes('armas')) {
    return {
      message: errorMessage, // Use the full message from backend (already user-friendly)
      type: 'moderation'
    };
  }

  // Erro de rate limit
  if (lower.includes('rate limit') || lower.includes('too many requests')) {
    return {
      message: 'O sistema está temporariamente sobrecarregado. Por favor, tente novamente em alguns minutos.',
      type: 'rate_limit'
    };
  }

  // Outros erros de API OpenAI - genérico
  if (lower.includes('openai api error') || lower.includes('openai')) {
    return {
      message: 'Ocorreu um erro no sistema de inteligência artificial. Por favor, tente reprocessar a task.',
      type: 'generic'
    };
  }

  // Retornar mensagem original se não encontrar padrão
  return { message: errorMessage, type: 'generic' };
}
